/**
 * Shared Quill config for support ticket flows (jobseeker).
 * Includes image upload: uploads to S3 via getPolicyForS3NonVideo + handleS3Upload,
 * then inserts image URL into editor. Content (text + images) is stored in DB and
 * visible to account managers and super admin.
 */
import { getPolicyForS3NonVideo } from 'src/apis/user';
import { handleS3Upload } from 'src/utils/imageUpload';

const SUPPORT_TICKET_S3_FOLDER = 'support-tickets';

function createSupportQuillImageHandler() {
	return function (this: any) {
		const quill = this.quill;
		const input = document.createElement('input');
		input.setAttribute('type', 'file');
		input.setAttribute('accept', 'image/*');
		input.onchange = async () => {
			const file = input.files?.[0];
			if (!file) return;
			try {
				const policy = await getPolicyForS3NonVideo(
					file.name,
					SUPPORT_TICKET_S3_FOLDER,
					file.type || 'image/png',
					true,
				);
				const url = await handleS3Upload(policy, file);
				if (url) {
					const range = quill.getSelection(true);
					quill.insertEmbed(range.index, 'image', url);
					quill.setSelection(range.index + 1);
				}
			} catch (err) {
				console.error('Support ticket image upload failed:', err);
			}
			input.value = '';
		};
		input.click();
	};
}

async function uploadImageToS3(file: File): Promise<string | null> {
	try {
		const policy = await getPolicyForS3NonVideo(
			file.name,
			SUPPORT_TICKET_S3_FOLDER,
			file.type || 'image/png',
			true,
		);
		return (await handleS3Upload(policy, file)) ?? null;
	} catch (err) {
		console.error('Support ticket image upload failed:', err);
		return null;
	}
}

/** Call after ReactQuill mounts: pasted images will upload to S3 and insert URL instead of Base64 */
export function attachSupportQuillPasteHandler(quill: any): () => void {
	if (!quill || !quill.root) return () => {};
	const root = quill.root;
	const handler = async (e: ClipboardEvent) => {
		const items = e.clipboardData?.items;
		if (!items) return;
		for (let i = 0; i < items.length; i++) {
			if (items[i].type.indexOf('image') !== -1) {
				e.preventDefault();
				const file = items[i].getAsFile();
				if (!file) return;
				const url = await uploadImageToS3(file);
				if (url) {
					const range = quill.getSelection(true) ?? { index: quill.getLength(), length: 0 };
					quill.insertEmbed(range.index, 'image', url);
					quill.setSelection(range.index + 1);
				}
				return;
			}
		}
	};
	root.addEventListener('paste', handler);
	return () => root.removeEventListener('paste', handler);
}

/** Toolbar with image upload (stored in S3, URL embedded in content; visible to AM/Admin) */
export const getSupportQuillModules = () => ({
	toolbar: {
		container: [
			[{ header: [1, 2, false] }],
			['bold', 'italic', 'underline', 'strike'],
			[{ list: 'ordered' }, { list: 'bullet' }],
			[{ align: [] }],
			['link', 'code-block'],
			['image'],
			['clean'],
		],
		handlers: {
			image: createSupportQuillImageHandler(),
		},
	},
});

export default getSupportQuillModules;
